import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { RoleService } from '../../services/role.service';
import { CommonModule } from '@angular/common';
import { LoginLayoutComponent } from '../../components/login-layout/login-layout.component';
import { LoginFormComponent } from '../../components/login-form/login-form.component';
import {
  SwitchAccountComponent,
  SavedUser,
} from '../../components/switch-account/switch-account.component';
import { ActiveAccountModalComponent } from '../../components/active-account-modal/active-account-modal.component';
import { Router } from '@angular/router';
import { timeout, catchError } from 'rxjs/operators';
import { throwError, TimeoutError } from 'rxjs';
import { getActiveUserDataRaw } from '../../shared/utils/impersonation-handoff.util';
import { UserRole } from '../../shared/types/role.interface';

/**
 * LoginComponent manages the authentication UI: it checks for an existing valid session on initialization, maintains a list of recently saved users for quick account switching, and exposes actions to force or cancel a login flow.
 * @component
 * @selector app-login
 * @example
 *   <app-login></app-login>
 * @input N/A none - This component does not define any @Input properties.
 * @output N/A none - This component does not emit any @Output events.
 *
 * Purpose:
 *   - Orchestrates the login page behavior including token validation, role-based redirection, cleanup of stale saved users, and toggling between saved-account UI and fresh login flow.
 *
 * Key properties:
 *   - alreadyLoggedIn: boolean - Flag indicating whether there are saved users to offer quick account reuse.
 *   - savedUsers: SavedUser[] - Array of recently saved users (filtered to 30 days) shown for account switching.
 *
 * Injected services:
 *   - authService: AuthService - Used to validate tokens, obtain current user info, and control force-login/cancel actions.
 *   - router: Router - Used for navigation after successful validation or role-based redirection.
 *   - roleService: RoleService - Used to determine default routes for users based on role.
 *
 * Important lifecycle hooks:
 *   - ngOnInit(): void
 *       - Runs only in browser contexts (guards against server-side execution).
 *       - Reads 'userData' from localStorage and, if present, calls authService.validateToken() to verify it.
 *         On success, relies on authService.user() and roleService.getDefaultRoute() to navigate to a role-appropriate route (falls back to /dashboard).
 *       - Reads 'saved_user' from localStorage, filters out entries older than 30 days (retains entries without date for backward compatibility), persists the trimmed list, and sets alreadyLoggedIn when entries exist.
 *
 * Key methods:
 *   - handleUseAnotherAccount(): void
 *       - Resets the alreadyLoggedIn flag to false to switch the UI to use another account mode.
 *
 *   - onProceed(): void
 *       - Triggers authService.forceLogin() to initiate a force-login flow (subscribes and logs errors if the request fails).
 *
 *   - onCancel(): void
 *       - Calls authService.cancelForceLogin() to cancel any ongoing force-login flow.
 *
 * Error handling and notes:
 *   - validateToken() errors are intentionally ignored to keep the user on the login page when the token is invalid/expired.
 *   - Force-login errors are logged to the console but not surfaced to the UI by this component.
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    LoginLayoutComponent,
    LoginFormComponent,
    SwitchAccountComponent,
    ActiveAccountModalComponent,
  ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  /** True while validating an existing session so we do not flash the login form. */
  sessionCheckPending = false;
  alreadyLoggedIn = false;
  savedUsers: SavedUser[] = [];

  constructor(
    public authService: AuthService,
    private router: Router,
    private roleService: RoleService
  ) {}

  /**
   * Initialize the login component: when running in a browser, check stored user data and validate the auth token to perform role-based redirection (or navigate to /dashboard if no role); prune 'saved_user' entries older than 30 days, update the savedUsers list, and set alreadyLoggedIn accordingly.
   * @example
   * gOnInit()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Prefer impersonation session over normal shop userData.
    const stored = getActiveUserDataRaw();
    if (stored) {
      let session: Record<string, any> | null = null;
      try {
        session = JSON.parse(stored);
      } catch {
        session = null;
      }

      // Already impersonating — send back to the shop home immediately.
      if (session?.['impersonated'] && session?.['token']) {
        if (!this.authService.user()?.token) {
          this.authService.user.set(session);
        }
        const rawRole = session['role'];
        const role =
          typeof rawRole === 'string' && (Object.values(UserRole) as string[]).includes(rawRole)
            ? (rawRole as UserRole)
            : UserRole.ADMIN;
        this.roleService.setUserRole(role);
        void this.router.navigate([this.roleService.getDefaultRoute(role)]);
        return;
      }

      if (session?.['token']) {
        this.sessionCheckPending = true;
        this.authService
          .validateToken()
          .pipe(
            timeout(8000),
            catchError(err => {
              if (err instanceof TimeoutError) {
                return throwError(() => new Error('Session check timed out'));
              }
              return throwError(() => err);
            })
          )
          .subscribe({
            next: () => {
              this.sessionCheckPending = false;
              const userData = this.authService.user();
              const rawRole = userData?.role;
              const role =
                typeof rawRole === 'string' &&
                (Object.values(UserRole) as string[]).includes(rawRole)
                  ? (rawRole as UserRole)
                  : null;
              if (role) {
                void this.router.navigate([this.roleService.getDefaultRoute(role)]);
              } else {
                void this.router.navigate(['/dashboard']);
              }
            },
            error: () => {
              this.sessionCheckPending = false;
              this.loadSavedAccountsForLogin();
            },
          });
        return;
      }
    }

    this.loadSavedAccountsForLogin();
  }

  private loadSavedAccountsForLogin(): void {
    const savedUsersData = localStorage.getItem('saved_user');
    if (!savedUsersData) {
      return;
    }

    let allUsers: SavedUser[];
    try {
      allUsers = JSON.parse(savedUsersData);
      if (!Array.isArray(allUsers)) {
        allUsers = [];
      }
    } catch {
      // Corrupt localStorage entry — clear it so it does not block login again.
      localStorage.removeItem('saved_user');
      return;
    }
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentUsers = allUsers.filter(user => {
      if (user.date) {
        const userDate = new Date(user.date);
        return userDate > thirtyDaysAgo;
      }
      return true;
    });

    localStorage.setItem('saved_user', JSON.stringify(recentUsers));

    this.savedUsers = recentUsers;
    if (this.savedUsers.length > 0) {
      this.alreadyLoggedIn = true;
    }
  }

  handleUseAnotherAccount() {
    this.alreadyLoggedIn = false;
  }

  onProceed(): void {
    this.authService.forceLogin().subscribe({
      error: err => {
        console.error('Force login failed:', err);
      },
    });
  }

  onCancel(): void {
    this.authService.cancelForceLogin();
  }
}
